import { RESPONSE_TIME } from './demo';
import { pricing, usd } from './pricing';
import { requirementsSummary } from './requirements';

/**
 * Full FAQ for /faq; the six flagged `inline` also appear on the homepage
 * (plan/04 §07, plan/05 §5). Each `q` is phrased as the real search query and
 * renders as an <h3> (plan/08 §4). Prices interpolate from the single source.
 */
export interface Faq {
	id: string;
	q: string;
	a: string;
	inline?: boolean;
}

export const faqs: Faq[] = [
	{
		id: 'what',
		inline: true,
		q: 'What exactly am I buying today?',
		a: `A presale license: 12 months of full PPF Engine access for ${usd(pricing.founderYear)}, instead of the ${usd(pricing.annual)}/yr public launch price. You also lock the same ${usd(pricing.founderRenewal)} annual renewal price for this one-seat license. You’re charged today; your year starts the day we deliver (estimated ${pricing.deliveryWindow}), not today. Until your access begins, you can get a full refund with one email.`
	},
	{
		id: 'subscription',
		inline: true,
		q: 'Is this a subscription? Does it auto-renew?',
		a: `Your presale purchase is a prepaid first year. It does not auto-renew and we will not charge you again automatically. Near the end, you choose whether to renew the same one-seat license annually for the founder price of ${usd(pricing.founderRenewal)}, switch to an available shorter term at its then-current price, or walk away. Your ${usd(pricing.founderRenewal)} annual founder price stays locked.`
	},
	{
		id: 'seats',
		inline: true,
		q: 'How many computers or people can use it?',
		a: `A license includes one seat. The locked ${usd(pricing.founderRenewal)} annual renewal price applies to that original founder license. Additional seats can be purchased after launch at the prices offered for those seats.`
	},
	{
		id: 'late',
		inline: true,
		q: 'What if it’s late, or never ships?',
		a: `If we miss the window you’ll get the choice: keep waiting or take an instant full refund. The people behind this cut film for a living; the tool exists because we needed it ourselves.`
	},
	{
		id: 'installers-only',
		inline: true,
		q: 'Do you sell this to film manufacturers?',
		a: `No. Licenses go to installation shops and independent installers only, never to PPF film manufacturers. And because it’s a desktop app, your scans and templates stay on your machine; nothing is uploaded to us or anyone else.`
	},
	{
		id: 'plotters',
		inline: true,
		q: 'Which plotters are supported?',
		a: `Export is SVG, DXF and PLT (HP-GL), developed and tested against a Graphtec FC9000. If your cutter reads any of those, you’re covered. Files are full-scale in millimetres with a 100 mm reference line so the scale is never in question.`
	},
	{
		id: 'demo-first',
		inline: true,
		q: 'Can I see PPF Engine work before I pre-order?',
		a: `Yes. Request a workflow demo and tell us the vehicle or panel your pattern library does not cover, plus your scanner and plotter. A person replies within ${RESPONSE_TIME} with a demo of the real software on a comparable panel, recorded or as a live screen share. It costs nothing and does not put you on a sales sequence.`
	},
	{
		id: 'availability',
		q: 'Can I download and use PPF Engine today?',
		a: `Not yet. The software runs in our own PPF studio, where the jobs shown on this site were scanned, flattened and cut, but it is not released for download. Presale buyers get access at delivery, estimated ${pricing.deliveryWindow}, and their 12 months start that day. Until then the honest ways to evaluate it are a workflow demo and the documented jobs on this site.`
	},
	{
		id: 'tested-hardware',
		q: 'Which equipment have you actually tested, and which is only file-compatible?',
		a: `Tested in our own studio: Shining 3D Einstar Rockit and EinScan Rigil scanners and a Graphtec FC9000 plotter. File-compatible: any plotter or cutting software that reads SVG, DXF or PLT (HP-GL), and any scanner that exports an STL, OBJ or PLY mesh. File-compatible means the format matches, not that we have run that exact device. Name your scanner and plotter in a demo request and we will tell you what we know about that combination.`
	},
	{
		id: 'test-fit',
		q: 'Do I still need to test fit a pattern made from a scan?',
		a: `Yes. PPF Engine shows stretch, scale and edge problems before you cut, which removes most surprises, but scan quality and how far you wrap or stretch the film still vary. Cut a test piece, check it on the panel, correct the pattern in the editor, then cut production film. The corrected pattern is yours to reuse.`
	},
	{
		id: 'scanners',
		q: 'What do I need to scan cars?',
		a: `Any scanner that exports STL, OBJ or PLY. We use a Shining 3D Einstar Rockit, an entry-level handheld, and an EinScan Rigil laser handheld; anything in that class or better is a good starting point. Installers use everything from entry-level structured-light handhelds to professional laser scanners. The cleanup tools are built for noisy, real-world scans.`
	},
	{
		id: 'make-own-patterns',
		q: 'How do I make my own PPF patterns from a 3D scan?',
		a: `Import an STL, OBJ or PLY scan, define coverage and seams on the real panel, flatten it, then inspect stretch, reliefs and edge wraps before exporting SVG, DXF or PLT for your plotter. PPF Engine handles that digital workflow; you still make a physical test fit before production cutting.`
	},
	{
		id: 'replace-database',
		q: 'Does PPF Engine replace my PPF pattern database?',
		a: `Not for ordinary jobs where your trusted database pattern already fits. It is for new, rare, modified or custom vehicles, missing panels, unusual coverage and database patterns that need correcting: the jobs where waiting for a catalog update costs you the booking.`
	},
	{
		id: 'requirements',
		q: 'What are the computer requirements?',
		a: requirementsSummary
	},
	{
		id: 'edit',
		q: 'Can I edit the templates it makes?',
		a: `Yes. Illustrator-style anchor and handle editing is built in, corners round off clean automatically, and everything exports as clean vectors.`
	},
	{
		id: 'edges',
		q: 'Does it handle wrapped edges and tuck?',
		a: `Yes. Edge-wrap offsets with fold layers, relief cuts, and pre-stretch tools are part of the pipeline, alongside a wrinkle tester and install simulator.`
	},
	{
		id: 'worldwide',
		q: 'Do you sell outside the US?',
		a: `Yes, worldwide. Prices are in US dollars; your bank converts at card rates. Taxes (EU VAT, US sales tax where it applies) are calculated at checkout, and EU businesses can enter a VAT ID for reverse charge.`
	},
	{
		id: 'who',
		q: 'Who’s behind this?',
		a: `Andify OÜ: installers and software people in Tartu, Estonia. You’ll get build notes from the actual developers, not a newsletter robot.`
	}
];

export const inlineFaqs = faqs.filter((f) => f.inline);
